import * as React from "react";
import {useMemo, useState} from "react";
import {IPlansTabResponse} from "../../../../api/AdminAnalyticsApi";
import {SortDir} from "../../../../utils/listUtil";
import {sortTh} from "../shared/SortableTh.component";
import {CLIENT_ADOPTION_DESCRIPTION} from "../shared/analytics.constants";
import {PlanDrilldownTable} from "./PlanDrilldownTable.component";
import {usePlanDrilldown} from "./usePlanDrilldown";
import {numberWithCommas} from "../../../../utils/numberUtil";

interface IClientAdoptionTableProps {
    rows: IPlansTabResponse["clientAdoption"];
    range: { startDate: string; endDate: string };
    country: string;
}

export const ClientAdoptionTable: React.FC<IClientAdoptionTableProps> = (props) => {
    const [clientAdoptionSort, setClientAdoptionSort] = useState<{ col: string; dir: SortDir }>({
        col: "clientPercent",
        dir: "desc",
    });
    const [showAllClientAdoption, setShowAllClientAdoption] = useState<boolean>(false);
    const drilldown = usePlanDrilldown(props.range, props.country);

    const sortedClientAdoption = useMemo(() => {
        const arr = [...props.rows];
        const {col, dir} = clientAdoptionSort;
        const mult = dir === "asc" ? 1 : -1;
        arr.sort((a, b) => {
            const av = col === "planName" ? a.planName.toLowerCase() : (a as any)[col];
            const bv = col === "planName" ? b.planName.toLowerCase() : (b as any)[col];
            if (col === "planName") return mult * (av < bv ? -1 : av > bv ? 1 : 0);
            return mult * ((av as number) - (bv as number));
        });
        return arr;
    }, [props.rows, clientAdoptionSort]);

    const avgClientPct = props.rows.length > 0
        ? Math.round(props.rows.reduce((sum, r) => sum + r.clientPercent, 0) / props.rows.length)
        : 0;

    return (
        <section className="admin-analytics__section">
            <div className="admin-analytics__section-header">
                <h2 className="admin-analytics__section-title">Client User Adoption by Plan</h2>
                <span className="admin-analytics__section-subtitle">
                    Client vs. internal user breakdown per plan
                </span>
            </div>
            <p className="admin-analytics__section-desc">{CLIENT_ADOPTION_DESCRIPTION}</p>
            <div className="admin-analytics__summary-grid">
                <div className="admin-analytics__summary-card admin-analytics__summary-card--total">
                    <div className="admin-analytics__summary-label">Plans with Clients</div>
                    <div className="admin-analytics__summary-value">
                        {props.rows.filter((r) => r.clientUsers > 0).length}
                    </div>
                    <div className="admin-analytics__summary-note">plans with at least one client user
                    </div>
                </div>
                <div className="admin-analytics__summary-card admin-analytics__summary-card--internal">
                    <div className="admin-analytics__summary-label">Total Client Users</div>
                    <div className="admin-analytics__summary-value">
                        {numberWithCommas(props.rows.reduce((sum, r) => sum + r.clientUsers, 0))}
                    </div>
                    <div className="admin-analytics__summary-note">unique client users across all
                        plans
                    </div>
                </div>
                <div className="admin-analytics__summary-card admin-analytics__summary-card--external">
                    <div className="admin-analytics__summary-label">Avg Client %</div>
                    <div className="admin-analytics__summary-value">{avgClientPct}%</div>
                    <div className="admin-analytics__summary-note">average across plans</div>
                </div>
            </div>
            <table className="admin-analytics__table admin-analytics__table--clickable">
                <thead>
                <tr>
                    {sortTh("planName", "Plan", clientAdoptionSort, setClientAdoptionSort, undefined, "asc")}
                    {sortTh("internalUsers", "Internal", clientAdoptionSort, setClientAdoptionSort,
                        "admin-analytics__cell--right")}
                    {sortTh("clientUsers", "Client", clientAdoptionSort, setClientAdoptionSort,
                        "admin-analytics__cell--right")}
                    {sortTh("totalUsers", "Total", clientAdoptionSort, setClientAdoptionSort,
                        "admin-analytics__cell--right")}
                    {sortTh("clientPercent", "Client %", clientAdoptionSort, setClientAdoptionSort,
                        "admin-analytics__cell--right")}
                </tr>
                </thead>
                <tbody>{(showAllClientAdoption
                    ? sortedClientAdoption
                    : sortedClientAdoption.slice(0, 20)).map((row) => (
                    <React.Fragment key={row.planName}>
                        <tr
                            className={`admin-analytics__row--expandable${
                                drilldown.expanded === row.planName ? " admin-analytics__row--expanded" : ""}`}
                            onClick={() => drilldown.toggle(row.planName)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") drilldown.toggle(row.planName);
                            }}
                        >
                            <td>
                                <i className={`far ${
                                    drilldown.expanded === row.planName ? "fa-chevron-down" : "fa-chevron-right"
                                } admin-analytics__expand-icon`}
                                   aria-hidden="true"/>
                                {row.planName}
                            </td>
                            <td className="admin-analytics__cell--right">{numberWithCommas(row.internalUsers)}</td>
                            <td className="admin-analytics__cell--right">{numberWithCommas(row.clientUsers)}</td>
                            <td className="admin-analytics__cell--right">{numberWithCommas(row.totalUsers)}</td>
                            <td className="admin-analytics__cell--right">{row.clientPercent}%</td>
                        </tr>
                        {drilldown.expanded === row.planName && (
                            <tr className="admin-analytics__drilldown-row">
                                <td colSpan={5}>
                                    <PlanDrilldownTable
                                        data={drilldown.data}
                                        loading={drilldown.loading}
                                        colSpan={5}
                                        showUserColumns
                                        loadingMessage="Loading plan details..."
                                    />
                                </td>
                            </tr>
                        )}
                    </React.Fragment>
                ))}</tbody>
            </table>
            {props.rows.length > 20 && (
                <button className="admin-analytics__show-more-btn"
                        onClick={() => setShowAllClientAdoption(!showAllClientAdoption)}>
                    {showAllClientAdoption ? "Show Top 20" : `Show All ${props.rows.length} Plans`}
                </button>
            )}
        </section>
    );
};
